import { Link } from "@tanstack/react-router";
import type { Post } from "@/data/posts";
import { categorySlug, siteConfig } from "@/config";
import { formatDate } from "@/lib/format";
import { sizedImage } from "@/lib/image";

export function PostCard({ post, priority = false }: { post: Post; priority?: boolean }) {
  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-border bg-card shadow-sm transition-shadow hover:shadow-md">
      <Link
        to="/post/$slug"
        params={{ slug: post.slug }}
        className="block overflow-hidden"
        aria-label={post.title}
      >
        <div className="aspect-[16/9] w-full overflow-hidden bg-muted">
          <img
            src={sizedImage(post.thumbnail, 800)}
            alt={post.title}
            width={800}
            height={450}
            loading={priority ? "eager" : "lazy"}
            decoding="async"
            {...(priority ? { fetchPriority: "high" as const } : {})}
            sizes="(min-width: 1024px) 400px, (min-width: 768px) 50vw, 100vw"
            onError={(e) => {
              const t = e.currentTarget;
              if (t.src !== siteConfig.fallbackImage) t.src = siteConfig.fallbackImage;
            }}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
      </Link>
      <div className="flex flex-1 flex-col p-5">
        <Link
          to="/category/$category"
          params={{ category: categorySlug(post.category) }}
          className="mb-2 inline-flex w-fit rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary hover:bg-primary/15"
        >
          {post.category}
        </Link>
        <h3 className="text-lg font-semibold leading-snug tracking-tight text-foreground">
          <Link to="/post/$slug" params={{ slug: post.slug }} className="hover:text-primary">
            {post.title}
          </Link>
        </h3>
        <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
          <img
            src="/admin-avatar.webp"
            alt="Admin avatar"
            width={20}
            height={20}
            className="inline-block h-5 w-5 rounded-full border border-border object-cover"
            loading="lazy"
            decoding="async"
          />
          <span>By {post.author} • {formatDate(post.publishDate)}</span>
        </div>
        <p className="mt-3 line-clamp-3 text-sm text-muted-foreground">{post.excerpt}</p>
        <Link
          to="/post/$slug"
          params={{ slug: post.slug }}
          className="mt-4 inline-flex items-center text-sm font-semibold text-primary hover:underline"
        >
          Read More →
        </Link>
      </div>
    </article>
  );
}
